/**
 * Authentication API service for multi-tenant Okta authentication
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export interface DomainDiscoveryRequest {
  email: string;
}

export interface DomainDiscoveryResponse {
  email_domain: string;
  has_okta_config: boolean;
  organization_name?: string;
  custom_settings?: {
    logo_url?: string;
    brand_colors?: { primary: string; secondary?: string };
  };
  okta_domain?: string;
}

export interface OAuthLoginRequest {
  provider: 'google' | 'microsoft' | 'okta';
  code: string;
  redirect_uri: string;
  state?: string;
  email_domain?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  user: UserResponse;
}

export interface UserResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  status: string;
  role: string;
  is_email_verified: boolean;
  tenant_id: string;
  organization?: string;
  created_at: string;
  last_login_at?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

class AuthApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Discover Okta configuration for a user's email domain
   */
  async discoverDomain(email: string): Promise<DomainDiscoveryResponse> {
    const response = await axios.post<DomainDiscoveryResponse>(
      `${API_BASE}/api/auth/okta/discover`,
      { email }
    );
    return response.data;
  }

  /**
   * Get OAuth authorization URL for Google
   */
  async getGoogleAuthUrl(redirectUri: string, state?: string): Promise<{ authorization_url: string }> {
    console.log('🔍 getGoogleAuthUrl called with:', { redirectUri, state, API_BASE });
    
    const params = new URLSearchParams({
      redirect_uri: redirectUri,
      ...(state && { state })
    });

    const fullUrl = `${API_BASE}/api/auth/oauth/google/authorize?${params}`;
    console.log('🔍 Making request to:', fullUrl);

    try {
      const response = await axios.get<{ authorization_url: string }>(fullUrl);
      console.log('✅ Google auth URL response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Google auth URL error:', error);
      throw error;
    }
  }

  /**
   * Get OAuth authorization URL for Microsoft
   */
  async getMicrosoftAuthUrl(redirectUri: string, state?: string): Promise<{ authorization_url: string }> {
    console.log('🔍 getMicrosoftAuthUrl called with:', { redirectUri, state, API_BASE });
    
    const params = new URLSearchParams({
      redirect_uri: redirectUri,
      ...(state && { state })
    });

    const fullUrl = `${API_BASE}/api/auth/oauth/microsoft/authorize?${params}`;
    console.log('🔍 Making request to:', fullUrl);

    try {
      const response = await axios.get<{ authorization_url: string }>(fullUrl);
      console.log('✅ Microsoft auth URL response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Microsoft auth URL error:', error);
      throw error;
    }
  }

  /**
   * Get OAuth authorization URL for Okta (dynamic based on email domain)
   */
  async getOktaAuthUrl(
    email: string, 
    redirectUri: string, 
    state?: string
  ): Promise<{ authorization_url: string; email_domain: string; organization_name: string }> {
    const params = new URLSearchParams({
      email,
      redirect_uri: redirectUri,
      ...(state && { state })
    });

    const response = await axios.get<{ 
      authorization_url: string; 
      email_domain: string; 
      organization_name: string 
    }>(`${API_BASE}/api/auth/okta/oauth/okta/authorize?${params}`);
    return response.data;
  }

  /**
   * Complete OAuth login with authorization code
   */
  async completeOAuthLogin(loginRequest: OAuthLoginRequest): Promise<TokenResponse> {
    const response = await axios.post<TokenResponse>(
      `${API_BASE}/api/auth/oauth/login`,
      loginRequest
    );
    return response.data;
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await axios.post<TokenResponse>(
      `${API_BASE}/api/auth/refresh`,
      { refresh_token: refreshToken }
    );
    return response.data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await axios.post(
        `${API_BASE}/api/auth/logout`,
        {},
        { headers: this.getAuthHeaders() }
      );
    } finally {
      // Clear tokens regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<UserResponse> {
    const response = await axios.get<UserResponse>(
      `${API_BASE}/api/auth/me`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    try {
      // Simple token expiry check (JWT payload contains 'exp')
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  /**
   * Get stored user data
   */
  getCurrentUserFromStorage(): UserResponse | null {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Store authentication data
   */
  storeAuthData(tokenResponse: TokenResponse): void {
    localStorage.setItem('access_token', tokenResponse.access_token);
    if (tokenResponse.refresh_token) {
      localStorage.setItem('refresh_token', tokenResponse.refresh_token);
    }
    localStorage.setItem('user_data', JSON.stringify(tokenResponse.user));
  }

  /**
   * Clear authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
  }
}

export const authApi = new AuthApiService();
